/**
 * B2B Wholesale Order Page — b2b-order-mail.js
 *
 * Features:
 *  - ▲▼ stepper buttons for each size / variant
 *  - Real-time subtotal & grand total calculation
 *  - Category filter
 *  - Sticky order bar (appears when any qty > 0)
 *  - Image lightbox
 *  - SEND ORDER → order summary modal (with UPC)
 */

(function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────────── */
  const config = window.B2B_CONFIG || {
    discountRate: 30,
    currency: 'JPY',
    moneyFormat: '¥{{amount}}',
    customerName: '',
    pageTitle: 'WHOLESALE ORDER'
  };

  /* ── DOM references ──────────────────────────────────────── */
  const page       = document.querySelector('.b2b-page');
  const cartBar    = document.getElementById('b2b-cart-bar');
  const totalItems = document.getElementById('b2b-total-items');
  const totalPrice = document.getElementById('b2b-total-price');
  const addBtn     = document.getElementById('b2b-add-to-cart');
  const modal      = document.getElementById('b2b-modal');
  const modalClose = document.getElementById('b2b-modal-close');
  const summaryEl  = document.getElementById('b2b-order-summary');
  const summaryItemsEl = document.getElementById('b2b-summary-items');
  const summaryTotalEl = document.getElementById('b2b-summary-total');
  const filterBtns = document.querySelectorAll('.b2b-filter__btn, .b2b-filter__cat');

  if (!page) return;

  const discountRate = parseInt(page.dataset.discount, 10) || config.discountRate;

  /* ── Format money ────────────────────────────────────────── */
  function formatMoney(cents) {
    if (config.currency === 'JPY') {
      return '¥' + Math.round(cents / 100).toLocaleString('ja-JP');
    }
    const amount = Math.round(cents) / 100;
    return config.moneyFormat.replace('{{amount}}', amount.toFixed(2));
  }

  /* ── Stepper helpers ─────────────────────────────────────── */
  function getValue(input) {
    return Math.max(0, parseInt(input.value, 10) || 0);
  }

  function setValue(input, val) {
    input.value = Math.max(0, val);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ── Row subtotal ────────────────────────────────────────── */
  function updateRowSubtotal(row) {
    const price  = parseInt(row.dataset.price, 10) || 0;
    const inputs = row.querySelectorAll('.b2b-stepper__input');
    let rowQty   = 0;
    inputs.forEach(function (inp) { rowQty += getValue(inp); });

    const sub = row.querySelector('[data-row-subtotal]');
    if (sub) sub.textContent = formatMoney(price * rowQty);

    row.classList.toggle('has-qty', rowQty > 0);
  }

  /* ── Grand total ─────────────────────────────────────────── */
  function updateGrandTotal() {
    const rows     = document.querySelectorAll('.b2b-row');
    let grandCents = 0;
    let grandQty   = 0;

    rows.forEach(function (row) {
      const price  = parseInt(row.dataset.price, 10) || 0;
      const inputs = row.querySelectorAll('.b2b-stepper__input');
      inputs.forEach(function (inp) {
        const qty   = getValue(inp);
        grandQty   += qty;
        grandCents += price * qty;
      });
    });

    totalItems.textContent = grandQty;
    totalPrice.textContent = formatMoney(grandCents);

    const visible = grandQty > 0;
    cartBar.classList.toggle('is-visible', visible);
    cartBar.setAttribute('aria-hidden', String(!visible));
  }

  /* ── Attach stepper events ───────────────────────────────── */
  function attachStepperEvents(rows) {
    rows.forEach(function (row) {
      row.querySelectorAll('.b2b-size-item__stepper').forEach(function (stepper) {
        const upBtn   = stepper.querySelector('.b2b-stepper__btn--up');
        const downBtn = stepper.querySelector('.b2b-stepper__btn--down');
        const input   = stepper.querySelector('.b2b-stepper__input');
        if (!input) return;

        const stock = parseInt(input.dataset.stock, 10);

        function clamp(v) {
          v = Math.max(0, v);
          if (!isNaN(stock)) v = Math.min(stock, v);
          return v;
        }

        if (upBtn) upBtn.addEventListener('click', function () {
          const n = clamp(getValue(input) + 1);
          setValue(input, n);
          if (!isNaN(stock)) upBtn.disabled = n >= stock;
          if (downBtn) downBtn.disabled = false;
        });

        if (downBtn) downBtn.addEventListener('click', function () {
          const n = clamp(getValue(input) - 1);
          setValue(input, n);
          if (upBtn && !isNaN(stock)) upBtn.disabled = n >= stock;
          downBtn.disabled = n <= 0;
        });

        input.addEventListener('input', function () {
          const n = clamp(getValue(input));
          input.value = n;
          if (upBtn && !isNaN(stock)) upBtn.disabled = n >= stock;
          if (downBtn) downBtn.disabled = n <= 0;
          updateRowSubtotal(row);
          updateGrandTotal();
        });

        input.addEventListener('change', function () {
          updateRowSubtotal(row);
          updateGrandTotal();
        });
      });
    });
  }

  /* Initial attachment */
  attachStepperEvents(Array.from(document.querySelectorAll('.b2b-row')));

  /* ── Collection filter ───────────────────────────────────── */
  filterBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      filterBtns.forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');

      const col = btn.dataset.collection;
      document.querySelectorAll('.b2b-row').forEach(function (row) {
        row.classList.toggle('is-hidden', col !== 'all' && row.dataset.collection !== col);
      });
    });
  });

  /* ── Infinite scroll ─────────────────────────────────────── */
  (function initInfiniteScroll() {
    const nextPageEl = document.getElementById('b2b-next-page');
    if (!nextPageEl) return;

    const tbody    = document.querySelector('.b2b-table__body');
    const sentinel = document.createElement('tr');
    sentinel.style.height = '1px';
    tbody && tbody.appendChild(sentinel);

    let nextUrl   = nextPageEl.dataset.url;
    let isLoading = false;

    async function loadMore() {
      if (!nextUrl || isLoading) return;
      isLoading = true;

      try {
        const res  = await fetch(nextUrl);
        const text = await res.text();
        const doc  = new DOMParser().parseFromString(text, 'text/html');
        const rows = Array.from(doc.querySelectorAll('.b2b-row'));

        rows.forEach(function (r) { tbody.insertBefore(r, sentinel); });
        attachStepperEvents(rows);
        rows.forEach(function (row) { attachImageClicks(row); });

        const next = doc.getElementById('b2b-next-page');
        nextUrl = next ? next.dataset.url : null;
        if (!nextUrl) observer.disconnect();
      } catch (e) {
        console.error('[B2B] Infinite scroll error:', e);
      } finally {
        isLoading = false;
      }
    }

    const observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: '400px' });

    observer.observe(sentinel);
  })();

  /* ── Build order summary HTML ────────────────────────────── */
  function buildSummaryHTML() {
    const rows = document.querySelectorAll('.b2b-row');
    const lines = [];
    let grandCents = 0;
    let grandQty   = 0;

    rows.forEach(function (row) {
      const price   = parseInt(row.dataset.price, 10) || 0;
      const nameEl  = row.querySelector('.b2b-product-info__name');
      const skuEl   = row.querySelector('.b2b-product-info__sku');
      const name    = nameEl ? nameEl.textContent.trim() : '';
      const sku     = skuEl  ? skuEl.textContent.trim()  : '';

      const inputs = row.querySelectorAll('.b2b-stepper__input');

      inputs.forEach(function (inp) {
        const qty = getValue(inp);
        if (qty <= 0) return;

        const sizeItem  = inp.closest('.b2b-size-item');
        const labelEl   = sizeItem ? sizeItem.querySelector('.b2b-size-item__label') : null;
        const size      = labelEl ? labelEl.textContent.trim() : '';
        const barcode   = inp.dataset.barcode || '';
        const subtotal  = price * qty;

        grandCents += subtotal;
        grandQty   += qty;

        lines.push(
          '<tr>' +
          '<td><div class="b2b-summary__name">' + escHtml(name) + '</div>' +
          '<div class="b2b-summary__upc">SKU: ' + escHtml(sku) +
          (barcode ? ' &nbsp;|&nbsp; UPC: ' + escHtml(barcode) : '') + '</div></td>' +
          '<td>' + escHtml(size) + '</td>' +
          '<td>' + formatMoney(price) + '</td>' +
          '<td style="text-align:center">' + qty + '</td>' +
          '<td style="text-align:right">' + formatMoney(subtotal) + '</td>' +
          '</tr>'
        );
      });
    });

    if (!lines.length) return { html: '', grandCents: 0, grandQty: 0 };

    const html =
      '<table class="b2b-summary-table">' +
      '<thead><tr>' +
      '<th>PRODUCT</th><th>SIZE</th><th>WHOLESALE</th><th style="text-align:center">QTY</th><th style="text-align:right">SUBTOTAL</th>' +
      '</tr></thead>' +
      '<tbody>' + lines.join('') + '</tbody>' +
      '</table>';

    return { html: html, grandCents: grandCents, grandQty: grandQty };
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* ── Additional DOM refs ─────────────────────────────────── */
  const footerEl      = document.getElementById('b2b-modal-footer');
  const sentEl        = document.getElementById('b2b-modal-sent');
  const shareBtn      = document.getElementById('b2b-share-btn');
  const sentCloseBtn  = document.getElementById('b2b-modal-sent-close');
  const modalCustomer = document.getElementById('b2b-modal-customer');
  const inputName     = document.getElementById('b2b-input-name');
  const inputContact  = document.getElementById('b2b-input-contact');
  const errorName     = document.getElementById('b2b-error-name');
  const errorContact  = document.getElementById('b2b-error-contact');

  /* ── 入力欄の値を取得 ────────────────────────────────────── */
  function getCustomerName()    { return inputName    ? inputName.value.trim()    : ''; }
  function getCustomerContact() { return inputContact ? inputContact.value.trim() : ''; }

  /* ── バリデーション ──────────────────────────────────────── */
  function validateInputs() {
    let valid = true;

    if (!getCustomerName()) {
      if (inputName)  inputName.classList.add('is-error');
      if (errorName)  { errorName.classList.add('is-visible'); errorName.setAttribute('aria-hidden', 'false'); }
      valid = false;
    } else {
      if (inputName)  inputName.classList.remove('is-error');
      if (errorName)  { errorName.classList.remove('is-visible'); errorName.setAttribute('aria-hidden', 'true'); }
    }

    if (!getCustomerContact()) {
      if (inputContact) inputContact.classList.add('is-error');
      if (errorContact) { errorContact.classList.add('is-visible'); errorContact.setAttribute('aria-hidden', 'false'); }
      valid = false;
    } else {
      if (inputContact) inputContact.classList.remove('is-error');
      if (errorContact) { errorContact.classList.remove('is-visible'); errorContact.setAttribute('aria-hidden', 'true'); }
    }

    if (!valid && inputName && !getCustomerName()) {
      inputName.focus();
    } else if (!valid && inputContact && !getCustomerContact()) {
      inputContact.focus();
    }

    return valid;
  }

  /* 入力時にエラー表示をリセット */
  inputName && inputName.addEventListener('input', function () {
    if (getCustomerName()) {
      inputName.classList.remove('is-error');
      if (errorName) { errorName.classList.remove('is-visible'); errorName.setAttribute('aria-hidden', 'true'); }
    }
  });
  inputContact && inputContact.addEventListener('input', function () {
    if (getCustomerContact()) {
      inputContact.classList.remove('is-error');
      if (errorContact) { errorContact.classList.remove('is-visible'); errorContact.setAttribute('aria-hidden', 'true'); }
    }
  });

  /* ── Modal ───────────────────────────────────────────────── */
  function showModal() {
    if (!modal) return;
    /* サマリー表示状態にリセット */
    if (footerEl) footerEl.style.display = '';
    if (sentEl)   { sentEl.classList.remove('is-visible'); sentEl.setAttribute('aria-hidden', 'true'); }
    modal.classList.add('is-visible');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function hideModal() {
    if (!modal) return;
    modal.classList.remove('is-visible');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function showSentMessage() {
    if (footerEl) footerEl.style.display = 'none';
    if (sentEl)   { sentEl.classList.add('is-visible'); sentEl.setAttribute('aria-hidden', 'false'); }
  }

  if (modalClose)    modalClose.addEventListener('click', hideModal);
  if (sentCloseBtn)  sentCloseBtn.addEventListener('click', hideModal);
  if (modal) {
    modal.addEventListener('click', function (e) {
      if (e.target === modal) hideModal();
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') hideModal();
  });

  /* ── Build plain-text order for sharing ─────────────────── */
  function buildShareText() {
    const rows  = document.querySelectorAll('.b2b-row');
    const lines = [];
    const now   = new Date().toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });

    lines.push('【B2B WHOLESALE ORDER】');
    lines.push(config.pageTitle);
    lines.push(now);
    if (getCustomerName())    lines.push('氏名/店名: ' + getCustomerName());
    if (getCustomerContact()) lines.push('連絡先: '   + getCustomerContact());
    lines.push('割引: ' + discountRate + '% OFF');
    lines.push('');

    let grandCents = 0;
    let grandQty   = 0;

    rows.forEach(function (row) {
      const price  = parseInt(row.dataset.price, 10) || 0;
      const nameEl = row.querySelector('.b2b-product-info__name');
      const skuEl  = row.querySelector('.b2b-product-info__sku');
      const name   = nameEl ? nameEl.textContent.trim() : '';
      const sku    = skuEl  ? skuEl.textContent.trim()  : '';
      const inputs = row.querySelectorAll('.b2b-stepper__input');
      const sizeLines = [];
      let rowQty = 0;

      inputs.forEach(function (inp) {
        const qty = getValue(inp);
        if (qty <= 0) return;
        const sizeItem  = inp.closest('.b2b-size-item');
        const labelEl   = sizeItem ? sizeItem.querySelector('.b2b-size-item__label') : null;
        const size      = labelEl ? labelEl.textContent.trim() : '';
        const barcode   = inp.dataset.barcode || '';
        sizeLines.push('  ' + size + ' × ' + qty + (barcode ? ' (UPC:' + barcode + ')' : ''));
        rowQty     += qty;
        grandCents += price * qty;
        grandQty   += qty;
      });

      if (sizeLines.length) {
        lines.push(name + (sku ? ' [' + sku + ']' : ''));
        lines.push('  卸値: ' + formatMoney(price));
        sizeLines.forEach(function (l) { lines.push(l); });
        lines.push('  小計: ' + formatMoney(price * rowQty));
        lines.push('');
      }
    });

    lines.push('━━━━━━━━━━━━━━━━');
    lines.push('合計 ' + grandQty + ' points　' + formatMoney(grandCents));

    return { text: lines.join('\n'), grandQty: grandQty, grandCents: grandCents };
  }

  /* ── Discord Webhook ─────────────────────────────────────── */
  const DISCORD_WEBHOOK = 'https://discord.com/api/webhooks/1516374636634574888/iiButgS0rvBC9NsOsm2S6uLBAXpaXA67RoeMozenDNE7XadE-JdG5vBKcFVEu44SeuHi';

  async function sendToDiscord(text) {
    /* Discordは1メッセージ2000文字制限があるので分割して送る */
    const LIMIT   = 1900;
    const chunks  = [];
    let remaining = text;
    while (remaining.length > 0) {
      if (remaining.length <= LIMIT) {
        chunks.push(remaining);
        break;
      }
      /* 改行で切る */
      let cut = remaining.lastIndexOf('\n', LIMIT);
      if (cut <= 0) cut = LIMIT;
      chunks.push(remaining.slice(0, cut));
      remaining = remaining.slice(cut).trimStart();
    }

    for (let i = 0; i < chunks.length; i++) {
      const content = i === 0
        ? '```\n' + chunks[i] + '\n```'
        : '```\n' + chunks[i] + '\n```';
      await fetch(DISCORD_WEBHOOK, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ content: content })
      });
    }
  }

  /* ── Share button → Discord送信 ──────────────────────────── */
  shareBtn && shareBtn.addEventListener('click', async function () {
    const share = buildShareText();
    if (!share.grandQty) return;

    shareBtn.disabled = true;
    shareBtn.textContent = '送信中…';

    try {
      await sendToDiscord(share.text);
      showSentMessage();
    } catch (err) {
      console.error('[B2B] Discord send failed:', err);
      alert('送信に失敗しました。もう一度お試しください。\n' + err.message);
    } finally {
      shareBtn.disabled = false;
      shareBtn.textContent = 'SHARE ORDER';
    }
  });

  /* ── SEND ORDER button ───────────────────────────────────── */
  addBtn && addBtn.addEventListener('click', function () {
    if (!validateInputs()) return;

    const result = buildSummaryHTML();
    if (!result.grandQty) return;

    if (summaryEl)      summaryEl.innerHTML = result.html;
    if (summaryItemsEl) summaryItemsEl.textContent = result.grandQty + ' items';
    if (summaryTotalEl) summaryTotalEl.textContent = 'TOTAL  ' + formatMoney(result.grandCents);

    /* モーダルヘッダーに入力した顧客情報を表示 */
    if (modalCustomer) {
      modalCustomer.innerHTML =
        '<p class="b2b-modal__customer-name">' + escHtml(getCustomerName()) + '</p>' +
        '<p class="b2b-modal__customer-email">' + escHtml(getCustomerContact()) + '</p>';
    }

    showModal();
  });

  /* ── Lightbox ────────────────────────────────────────────── */
  const overlay = document.createElement('div');
  overlay.className = 'b2b-lightbox';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.setAttribute('role', 'dialog');

  const lbCloseBtn = document.createElement('button');
  lbCloseBtn.className = 'b2b-lightbox__close';
  lbCloseBtn.textContent = '×';
  lbCloseBtn.setAttribute('aria-label', 'Close');

  const lbImg = document.createElement('img');
  lbImg.className = 'b2b-lightbox__img';
  lbImg.alt = '';

  overlay.appendChild(lbCloseBtn);
  overlay.appendChild(lbImg);
  document.body.appendChild(overlay);

  function openLightbox(src, alt) {
    lbImg.src = src;
    lbImg.alt = alt || '';
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    overlay.classList.remove('is-visible');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { lbImg.src = ''; }, 200);
  }

  lbCloseBtn.addEventListener('click', closeLightbox);
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closeLightbox();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeLightbox();
      hideModal();
    }
  });

  function attachImageClicks(root) {
    (root || document).querySelectorAll('.b2b-product-info__img').forEach(function (el) {
      if (el.dataset.lightboxBound) return;
      el.dataset.lightboxBound = '1';
      el.addEventListener('click', function () {
        const src = el.dataset.full || (el.srcset || '').split(' ')[0] || el.src;
        openLightbox(src, el.alt);
      });
    });
  }

  attachImageClicks();

})();
